import { useCallback, useEffect, useState, type FunctionComponent } from 'react';
import styled from 'styled-components';
import {
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
  Text,
  Button,
  InformationMd,
} from '@property.com.au/ui-kit';

import { WebLink, WebLinkProps } from '../../../app-kit/components/WebLink/WebLink';
import {
  DynamicContents,
  DynamicContentsItem,
} from '../../../app-kit/components/DynamicContent/types';
import { useStreetPageModalContext } from '../../contexts/StreetPageModalContext/StreetPageModalContext';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';
import insights_banner_image from '../../../assets/suburb-insights-banner-light-bulb.svg';

const FADE_DURATION = 500;
const CYCLE_INTERVAL = 5000;

const BannerContainer = styled.div`
  background-color: ${paletteHelper('backgroundPrimary')};
  border-radius: ${radiusHelper('large')};
  padding: ${spacingHelper('medium')};
  width: 100%;
  display: flex;

  ${mediaHelper('tabletMin')} {
    align-items: center;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Insight = styled.div<{ $visible: boolean }>`
  margin-bottom: ${spacingHelper('small')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${FADE_DURATION}ms ease-in-out;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacingHelper('twoExtraSmall')};
`;

const DisclaimerText = styled(Text)`
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('small')};
`;

const SuburbWebLink = styled(WebLink)`
  ${typographyHelper('subtitle02')};
`;

const DotsContainer = styled.div`
  display: flex;
  gap: ${spacingHelper('twoExtraSmall')};
  margin-bottom: ${spacingHelper('small')};
`;

const Dot = styled.div<{ $active: boolean }>`
  width: ${spacingHelper('extraSmall')};
  height: ${spacingHelper('extraSmall')};
  border-radius: 50%;
  background-color: ${({ $active }) =>
    $active ? paletteHelper('textSecondary') : paletteHelper('borderSecondary')};
  transition: background-color ${FADE_DURATION}ms ease-in-out;
`;

const Image = styled.img`
  width: ${spacingHelper('threeExtraLarge')};
  height: ${spacingHelper('threeExtraLarge')};

  ${mediaHelper('tabletMin')} {
    width: 78px;
    height: 78px;
  }
`;

interface StreetPageSuburbInsightsBannerProps {
  title: string;
  insights: DynamicContents<DynamicContentsItem>[];
  cta: WebLinkProps;
  disclaimerText: string;
}

export const StreetPageSuburbInsightsBanner: FunctionComponent<
  StreetPageSuburbInsightsBannerProps
> = ({ title, insights, cta, disclaimerText }) => {
  const { displayModal } = useStreetPageModalContext();
  const [insightIndex, setInsightIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const cycleInsight = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setInsightIndex((prev) => (prev + 1) % insights.length);
      setVisible(true);
    }, FADE_DURATION);
  }, [insights.length]);

  useEffect(() => {
    if (insights.length <= 1) return;
    const intervalId = setInterval(cycleInsight, CYCLE_INTERVAL);
    return () => clearInterval(intervalId);
  }, [cycleInsight, insights.length]);

  const insightContent = insights[insightIndex];
  if (!insightContent) return null;

  return (
    <BannerContainer>
      <ContentContainer>
        <Title>
          <Text variant="subtitle01">{title}</Text>
          <Button
            variant="naked"
            sizeVariant="small"
            hideLabel
            compact
            icon={<InformationMd />}
            onClick={() => {
              displayModal({ name: 'demographicsDataDisclaimer' });
              trackDisclaimerModalOpen({
                inPageSource: 'street_page__suburb_insights_banner___disclaimer_button',
              });
            }}
          />
        </Title>
        <Insight $visible={visible}>
          <DynamicContent dynamicContents={insightContent} />
        </Insight>
        {insights.length > 1 && (
          <DotsContainer>
            {insights.map((_, index) => (
              <Dot key={index} $active={index === insightIndex} />
            ))}
          </DotsContainer>
        )}
        <SuburbWebLink {...cta} target="_blank" />
        <DisclaimerText variant="caption">{disclaimerText}</DisclaimerText>
      </ContentContainer>
      <Image src={insights_banner_image} alt="Suburb Insights Banner Image" />
    </BannerContainer>
  );
};
